import React, { useState, useEffect } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { toast } from 'react-toastify';
import moment from 'moment';
import * as adminService from '../../services/admin';

const TaxReports = () => {
    const [taxTransactions, setTaxTransactions] = useState([]);
    const [summary, setSummary] = useState([]);
    const [taxWiseSummary, setTaxWiseSummary] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState({
        branch_id: '',
        date_from: moment().startOf('month').format('YYYY-MM-DD'),
        date_to: moment().endOf('month').format('YYYY-MM-DD'),
        tax_type: ''
    });

    const taxTypes = ['GST', 'TDS', 'VAT', 'Service Tax'];

    useEffect(() => {
        loadBranches();
    }, []);

    const loadBranches = async () => {
        try {
            const response = await adminService.listBranches();
            if (response.status === 'success') {
                setBranches(response.data);
                if (response.data.length > 0) {
                    setFilter({ ...filter, branch_id: response.data[0].id });
                }
            }
        } catch (error) {
            console.error('Error loading branches:', error);
        }
    };

    const loadTaxReport = async () => {
        if (!filter.branch_id) {
            toast.error('Please select a branch');
            return;
        }

        try {
            setLoading(true);
            const response = await adminService.getTaxReport(filter);
            if (response.status === 'success') {
                setTaxTransactions(response.data.transactions);
                setSummary(response.data.summary);
                setTaxWiseSummary(response.data.tax_wise_summary);
            }
        } catch (error) {
            console.error('Error loading tax report:', error);
            toast.error('Failed to load tax report');
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilter({ ...filter, [name]: value });
    };

    const handleGenerateReport = () => {
        loadTaxReport();
    };

    const transactionColumns = [
        {
            headerName: 'Date',
            field: 'transaction_date',
            sortable: true,
            filter: true,
            width: 120,
            cellRenderer: (params) => moment(params.value).format('DD-MMM-YYYY')
        },
        {
            headerName: 'Voucher No.',
            field: 'voucher.voucher_number',
            sortable: true,
            filter: true,
            width: 140
        },
        {
            headerName: 'Account',
            field: 'ledger.account.account_name',
            sortable: true,
            filter: true,
            width: 200
        },
        {
            headerName: 'Tax Type',
            field: 'tax_type',
            sortable: true,
            filter: true,
            width: 110,
            cellRenderer: (params) => (
                <span className="badge bg-info">{params.value}</span>
            )
        },
        {
            headerName: 'Tax Name',
            field: 'tax_configuration.tax_name',
            sortable: true,
            filter: true,
            width: 150
        },
        {
            headerName: 'Tax Code',
            field: 'tax_configuration.tax_code',
            sortable: true,
            filter: true,
            width: 120
        },
        {
            headerName: 'Taxable Amount',
            field: 'taxable_amount',
            sortable: true,
            filter: true,
            width: 150,
            cellRenderer: (params) => `₹${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        },
        {
            headerName: 'Tax Rate',
            field: 'tax_rate',
            sortable: true,
            width: 100,
            cellRenderer: (params) => `${parseFloat(params.value).toFixed(2)}%`
        },
        {
            headerName: 'Tax Amount',
            field: 'tax_amount',
            sortable: true,
            filter: true,
            width: 140,
            cellRenderer: (params) => `₹${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        }
    ];

    return (
        <div className="container-fluid mt-3">
            <div className="card">
                <div className="card-header bg-primary text-white">
                    <h5 className="mb-0">
                        <i className="fa fa-chart-bar"></i> Tax Reports
                    </h5>
                </div>
                <div className="card-body">
                    {/* Filters */}
                    <div className="row mb-3">
                        <div className="col-md-3">
                            <label className="form-label fw-bold">Branch *</label>
                            <select
                                className="form-select"
                                name="branch_id"
                                value={filter.branch_id}
                                onChange={handleFilterChange}
                            >
                                <option value="">-- Select Branch --</option>
                                {branches.map(branch => (
                                    <option key={branch.id} value={branch.id}>
                                        {branch.branch_name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">From Date *</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_from"
                                value={filter.date_from}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">To Date *</label>
                            <input
                                type="date"
                                className="form-control"
                                name="date_to"
                                value={filter.date_to}
                                onChange={handleFilterChange}
                            />
                        </div>
                        <div className="col-md-2">
                            <label className="form-label fw-bold">Tax Type</label>
                            <select
                                className="form-select"
                                name="tax_type"
                                value={filter.tax_type}
                                onChange={handleFilterChange}
                            >
                                <option value="">All Types</option>
                                {taxTypes.map(type => (
                                    <option key={type} value={type}>{type}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-3 d-flex align-items-end">
                            <button
                                className="btn btn-primary w-100"
                                onClick={handleGenerateReport}
                                disabled={loading}
                            >
                                <i className="fa fa-cogs"></i> Generate Report
                            </button>
                        </div>
                    </div>

                    {/* Summary by Tax Type */}
                    {summary.length > 0 && (
                        <div className="mb-4">
                            <h6 className="mb-3">Summary by Tax Type</h6>
                            <div className="row">
                                {summary.map((item, index) => (
                                    <div key={index} className="col-md-3 mb-3">
                                        <div className="card bg-light">
                                            <div className="card-body">
                                                <h6 className="text-primary">{item.tax_type}</h6>
                                                <p className="mb-1">
                                                    <strong>Transactions:</strong> {item.transaction_count}
                                                </p>
                                                <p className="mb-1">
                                                    <strong>Taxable:</strong> ₹{parseFloat(item.total_taxable_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </p>
                                                <p className="mb-0">
                                                    <strong>Tax:</strong> ₹{parseFloat(item.total_tax_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tax-wise Summary */}
                    {taxWiseSummary.length > 0 && (
                        <div className="mb-4">
                            <h6 className="mb-3">Tax-wise Summary</h6>
                            <div className="table-responsive">
                                <table className="table table-bordered table-sm">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Tax Name</th>
                                            <th>Tax Code</th>
                                            <th>Rate (%)</th>
                                            <th className="text-end">Transactions</th>
                                            <th className="text-end">Taxable Amount</th>
                                            <th className="text-end">Tax Amount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {taxWiseSummary.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.tax_name}</td>
                                                <td><span className="badge bg-secondary">{item.tax_code}</span></td>
                                                <td>{parseFloat(item.tax_rate).toFixed(2)}%</td>
                                                <td className="text-end">{item.transaction_count}</td>
                                                <td className="text-end">
                                                    ₹{parseFloat(item.total_taxable_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </td>
                                                <td className="text-end">
                                                    ₹{parseFloat(item.total_tax_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Transaction Details */}
                    {taxTransactions.length > 0 && (
                        <div>
                            <h6 className="mb-3">Transaction Details</h6>
                            <div className="ag-theme-alpine" style={{ height: '400px', width: '100%' }}>
                                <AgGridReact
                                    rowData={taxTransactions}
                                    columnDefs={transactionColumns}
                                    pagination={true}
                                    paginationPageSize={20}
                                    domLayout='autoHeight'
                                />
                            </div>
                        </div>
                    )}

                    {taxTransactions.length === 0 && !loading && (
                        <div className="text-center text-muted py-5">
                            <i className="fa fa-info-circle fa-3x mb-3"></i>
                            <p>Select branch and date range, then click "Generate Report"</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default TaxReports;
